import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import type { UserData } from 'chaika-frontend-types/frontend-cms';

import { userKeys, userQueryOptions } from '@user/queries/userQueries';

export function useUsersMap(ids: string[]): Map<string, UserData> {
    return useUsersMapQuery(ids).map;
}

// `isFetched` is true on error too, so a failed request does not block a caller that waits.
export function useUsersMapQuery(ids: string[]): { map: Map<string, UserData>; isFetched: boolean } {
    const queryClient = useQueryClient();

    const { data, isFetched } = useQuery({
        ...userQueryOptions.list({ ids }),
        enabled: ids.length > 0,
    });

    const map = useMemo(() => {
        const requested = new Set(ids);
        const map = new Map<string, UserData>();

        // Seed from any cached list-query so cells stay populated while
        // refetching when `ids` change (filter / pagination / different rows
        // produce a new queryKey, for which `data` is briefly `undefined`).
        const cached = queryClient.getQueriesData<UserData[]>({ queryKey: userKeys.lists() });
        for (const [, users] of cached) {
            users?.forEach(user => {
                if (requested.has(user.id)) {
                    map.set(user.id, user);
                }
            });
        }

        // Overlay the freshest data on top.
        data?.forEach(user => map.set(user.id, user));

        return map;
    }, [data, queryClient, ids]);

    return { map, isFetched };
}
